/**
 * FeedbackButton — floating fixed-position button (bottom-right) opening a
 * beta-feedback modal. Rendered in AppLayout so it appears on EVERY page
 * (Landing, Teacher pages, Student pages, Admin, Instrukcja).
 *
 * Anonymous submission is fine (reporter_email/name optional). Submissions
 * hit POST /api/feedback which stores in db.feedback_submissions and emails
 * ADMIN_EMAIL via Resend/SMTP. Rate-limited to 5 per IP per hour.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquareWarning, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";

const CATEGORIES = ["bug", "suggestion", "question", "other"];

export default function FeedbackButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setCategory("bug");
    setSubject("");
    setMessage("");
    setReporterName("");
    setReporterEmail("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3 || message.trim().length < 10) return;
    setSending(true);
    try {
      await publicApi.post("/feedback", {
        category,
        subject: subject.trim(),
        message: message.trim(),
        reporter_name: reporterName.trim() || null,
        reporter_email: reporterEmail.trim() || null,
        page_url: window.location.href,
      });
      toast.success(t("feedback.toast_success"));
      setOpen(false);
      reset();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        toast.error(t("feedback.toast_rate_limit"));
      } else {
        toast.error(detail || t("feedback.toast_error"));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="fixed bottom-20 right-5 z-50 brut brut-hover bg-[#FEF08A]
                     border-2 border-[#0F172A] rounded-full h-12 px-4
                     font-bold text-sm flex items-center gap-2
                     shadow-[4px_4px_0px_#0F172A]"
          data-testid="feedback-trigger"
          aria-label={t("feedback.trigger_label")}
          title={t("feedback.trigger_label")}
        >
          <MessageSquareWarning className="w-5 h-5" strokeWidth={2.5} />
          <span className="hidden sm:inline">{t("feedback.trigger_short")}</span>
        </button>
      </DialogTrigger>
      <DialogContent
        className="brut border-2 border-[#0F172A] rounded-md bg-white max-w-lg"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-black">
            <MessageSquareWarning className="w-5 h-5" strokeWidth={2.5} />
            {t("feedback.modal_title")}
          </DialogTitle>
          <DialogDescription>{t("feedback.modal_desc")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="font-bold text-sm">{t("feedback.category_label")}</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={`brut brut-hover border-2 border-[#0F172A] rounded-md
                              px-3 py-2 text-xs font-bold text-left
                              ${category === cat ? "bg-[#FEF08A]" : "bg-white"}`}
                  data-testid={`feedback-category-${cat}`}
                >
                  {t(`feedback.category_${cat}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="fb-subject" className="font-bold text-sm">
              {t("feedback.subject_label")}
            </Label>
            <Input
              id="fb-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("feedback.subject_placeholder")}
              maxLength={200}
              className="mt-1 brut-input border-2 border-[#0F172A] rounded-md"
              data-testid="feedback-subject"
              required
            />
          </div>

          <div>
            <Label htmlFor="fb-message" className="font-bold text-sm">
              {t("feedback.message_label")}
            </Label>
            <Textarea
              id="fb-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("feedback.message_placeholder")}
              maxLength={5000}
              rows={5}
              className="mt-1 brut-input border-2 border-[#0F172A] rounded-md"
              data-testid="feedback-message"
              required
            />
            <div className="text-[10px] text-[#64748B] mt-1">
              {message.length}/5000
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="fb-name" className="font-bold text-xs">
                {t("feedback.reporter_name_label")}
              </Label>
              <Input
                id="fb-name"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
                maxLength={100}
                className="mt-1 brut-input border-2 border-[#0F172A] rounded-md text-sm"
                data-testid="feedback-name"
              />
            </div>
            <div>
              <Label htmlFor="fb-email" className="font-bold text-xs">
                {t("feedback.reporter_email_label")}
              </Label>
              <Input
                id="fb-email"
                type="email"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
                className="mt-1 brut-input border-2 border-[#0F172A] rounded-md text-sm"
                data-testid="feedback-email"
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="submit"
              disabled={sending || subject.trim().length < 3 || message.trim().length < 10}
              className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A]
                         text-white font-bold rounded-md w-full sm:w-auto
                         disabled:opacity-50"
              data-testid="feedback-submit"
            >
              <Send className="w-4 h-4 mr-2" strokeWidth={2.5} />
              {sending ? t("feedback.sending") : t("feedback.submit_button")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
